import axios from 'axios';

import Gateway from '..';
import { IdDocument, CreditCardFields, BuyerFields } from '../../../types';

import config from '../../config';

type MercadopagoDocType = {
  id: string;
  name: string;
  type: 'number' | 'text';
  min_length: string;
  max_length: string;
};

interface IMercadopago {
  setPublishableKey(key: string): void;
  getIdentificationTypes(): Promise<MercadopagoDocType[]>;
  getInstallments(params: {
    bin: string;
    amount: string;
    paymentTypeId: 'credit_card';
  }): Promise<
    {
      payment_method_id: string;
      payment_type_id: string;
      issuer: {
        id: string;
        name: string;
        secure_thumbnail: string;
      };
      payer_costs: {
        installments: number;
        recommended_message: string;
      }[];
    }[]
  >;
  createCardToken(_: {
    cardNumber: string;
    cardholderName: string;
    cardExpirationMonth: string;
    cardExpirationYear: string;
    securityCode: string;
    identificationType: string;
    identificationNumber: string;
  }): Promise<{
    id: string;
  }>;
  fields: {
    create: (
      name: string,
      options: any,
    ) => {
      mount: (id: string) => {
        on: (ev: 'binChange', cb: (_: { bin: string }) => void) => void;
      };
    };
  };
}

interface IMercadoPagoConstructor {
  new (publicKey: string): IMercadopago;
}

const w: { MercadoPago?: IMercadoPagoConstructor } & Window = window;

const loadLib = () =>
  new Promise<void>((resolve) => {
    if (!w.MercadoPago) {
      const s = document.createElement('script');
      s.src = 'https://sdk.mercadopago.com/js/v2';
      s.onload = () => resolve();
      document.body.append(s);
      return;
    }
    resolve();
  });

const fetchIdTypes = async (mpInstance: IMercadopago | null) => {
  const types = await mpInstance?.getIdentificationTypes();
  return types?.map((docType) => ({
    id: docType.id,
    name: docType.name,
    type: docType.type,
  }));
};

const MercadoPagoGateway: () => Gateway = () => {
  let idTypes: IdDocument[] | undefined;
  let mpInstance: IMercadopago | null = null;

  const getCardDetails: Gateway['getCardDetails'] = async (number, amount) => {
    const [response] = await mpInstance!.getInstallments({
      bin: number.substring(0, 8),
      amount: amount.toString(10),
      paymentTypeId: 'credit_card',
    });

    if (!response) {
      throw new Error('Failure when fetching installments! Response is falsy');
    }

    return {
      issuerId: response.issuer.id,
      issuerLogoUrl: response.issuer.secure_thumbnail,
      issuerName: response.issuer.name,
      methodId: response.payment_method_id,
      typeId: response.payment_type_id,
      installments: response.payer_costs.map((installment) => ({
        installments: installment.installments,
        description: installment.recommended_message,
      })),
    };
  };

  return {
    setup: async () => {
      await loadLib();
      mpInstance = new w.MercadoPago!(config.publicKey);
      idTypes = await fetchIdTypes(mpInstance);
    },
    getIdentificationTypes: () => idTypes,
    getCardDetails,
    validateField: (key, value) => {
      switch (key) {
        case CreditCardFields.cardNumber:
          return value.length === 16 || value.length === 15;
        case CreditCardFields.securityCode:
          return value.length === 3 || value.length === 4;
        case CreditCardFields.holderName:
          return !!value;
        case BuyerFields.documentNumber:
          switch (value.length) {
            case 11:
              const sum1 =
                (10 *
                  value
                    .substr(0, 9)
                    .split('')
                    .reduce(
                      (prev, current, index) => prev + Number(current) * (10 - index),
                      0,
                    )) %
                11;
              const sum2 =
                (10 *
                  value
                    .substr(0, 10)
                    .split('')
                    .reduce(
                      (prev, current, index) => prev + Number(current) * (11 - index),
                      0,
                    )) %
                11;
              return (
                !/1{11}|2{11}|3{11}|4{11}|5{11}|6{11}|7{11}|8{11}|9{11}/gi.test(value) &&
                (sum1 === Number(value[9]) || (sum1 === 10 && value[9] === '0')) &&
                (sum2 === Number(value[10]) || (sum2 === 10 && value[10] === '0'))
              );
            case 13:
              return true;
            default:
              return false;
          }
        case BuyerFields.email:
          return value.match(/@/gi)?.length === 1;
        default:
          return true;
      }
    },
    checkout: async (data) => {
      if (data.checkoutType === 'credit') {
        const { id: token } = await mpInstance!.createCardToken({
          cardNumber: data.cardNumber,
          cardholderName: data.holderName,
          cardExpirationMonth: data.expiryDate.split('/')[0],
          cardExpirationYear: data.expiryDate.split('/')[1],
          securityCode: data.securityCode,
          identificationType: data.documentType,
          identificationNumber: data.documentNumber,
        });

        await axios.post(`${config.endpointBaseUrl}/mercadopagoCheckout`, {
          token,
          installments: data.installments,
          email: data.email,
          methodId: data.paymentMethodId,
          documentType: data.documentType,
          documentNumber: data.documentNumber,
          productId: data.productId,
        });
      } else {
        await axios.post(`${config.endpointBaseUrl}/mercadopagoCheckout`, {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          documentType: data.documentType,
          documentNumber: data.documentNumber,
          productId: data.productId,
        });
      }
    },
  };
};

export default MercadoPagoGateway;
